import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SectionHeader } from './SiteChrome';

// Studio index — three 4×4 mosaics (photography, identity & design, web & app)
// with a dependency-free lightbox: focus-trapped overlay, arrow-key nav,
// Escape to close, body scroll lock. Tiles reuse the hardened reveal engine.

const g = (n, i, alt) => ({ src: `/ext/media/grid-${n}-${String(i).padStart(2, '0')}.jpg`, alt });

export const GRIDS = [
  {
    id: 'photography',
    kicker: 'Photography',
    title: 'The eye, at large.',
    line: 'Editorial photography in the studio\u2019s register — sea, plaster, light, and the things people actually make.',
    tiles: [
      { src: '/ext/media/photo-boat.jpg', alt: 'Wooden boat on clear turquoise water' },
      g('photo', 6, 'Rocky cove from above, turquoise shallows meeting deep blue'),
      { src: '/ext/media/photo-alley.jpg', alt: 'Whitewashed steps, turquoise railing, olive tree' },
      g('photo', 4, 'White chapel dome against a deep blue sky'),
      g('photo', 9, 'Turquoise taverna chairs on cobblestones'),
      g('photo', 5, 'Turquoise fishing nets coiled on a weathered dock'),
      g('photo', 8, 'Lemon branch over a whitewashed wall'),
      g('photo', 12, 'Weathered blue door in a plaster wall'),
      { src: '/ext/media/photo-table.jpg', alt: 'Olive branch and ceramic cups in morning light' },
      g('photo', 17, 'Octopus drying on a line over a sea wall'),
      g('photo', 7, 'White linen drying against a sunlit plaster wall'),
      g('photo', 14, 'Clear tide pools in a white limestone shelf'),
      g('photo', 18, 'Bougainvillea over a white wall with a turquoise window'),
      g('photo', 10, 'Sailboat rigging against a deep blue sky'),
      g('photo', 13, 'White cat asleep on whitewashed steps'),
      g('photo', 16, 'White windmill on a hilltop at golden hour'),
    ],
  },
  {
    id: 'identity',
    kicker: 'Identity & design',
    title: 'Marks, made physical.',
    line: 'Identity systems applied to the stuff a business actually touches — cards, packaging, signage, livery.',
    tiles: [
      g('design', 1, 'Deep blue business cards with a turquoise wave mark'),
      g('design', 7, 'Wave-motif art print pinned to a plaster wall'),
      g('design', 3, 'Turquoise wax seal with a wave emblem on cream paper'),
      g('design', 4, 'Kraft and deep blue packaging with embossed wave pattern'),
      g('design', 11, 'Color swatch fan in Aegean blues and sands'),
      g('design', 6, 'Brass shop plaque with an engraved wave emblem'),
      g('design', 2, 'Letterhead and envelope stationery suite'),
      g('design', 13, 'White van with a sweeping turquoise wave livery'),
      g('design', 10, 'Blind-embossed monogram on cotton paper'),
      g('design', 16, 'Olive oil bottle with a minimal wave label'),
      g('design', 8, 'Print proof sheets with registration marks and a loupe'),
      g('design', 14, 'Linen apron with an embroidered wave emblem'),
      g('design', 12, 'Letterpress wood type and a printed sheet in blue ink'),
      g('design', 15, 'Paper coasters and a matchbox with wave marks'),
      g('design', 9, 'Brand guidelines book spread in the studio palette'),
      g('design', 5, 'Canvas tote bag with a deep blue circular mark'),
    ],
  },
  {
    id: 'apps',
    kicker: 'Web & app',
    title: 'Software, shipped.',
    line: 'Booking engines, admin consoles, dashboards, and voice agents — designed here, built on the studio\u2019s foundation.',
    tiles: [
      g('app', 1, 'Laptop showing an elegant booking calendar interface'),
      g('app', 2, 'Phone showing a mobile app with stacked cards'),
      g('app', 3, 'Dark analytics dashboard with turquoise charts'),
      g('app', 13, 'Laptop by an open window showing a website hero'),
      g('app', 5, 'Two phones showing a booking flow and confirmation'),
      g('app', 9, 'Admin console with a data table and status pills'),
      g('app', 8, 'Voice assistant app with a glowing waveform'),
      g('app', 4, 'Tablet showing an image-grid portfolio interface'),
      g('app', 6, 'Wireframe sketches with the finished app on a phone'),
      g('app', 14, 'Dark-mode mobile interface with a turquoise gauge'),
      g('app', 11, 'Printed UI component sheet in the studio palette'),
      g('app', 12, 'Phone showing a payment success screen on a terrace'),
      g('app', 7, 'Design tool artboards of a mobile app'),
      g('app', 15, 'Tablet showing a map with turquoise routes'),
      g('app', 10, 'Terminal window with cascading turquoise glyphs'),
      g('app', 16, 'Desktop and phone showing the same responsive interface'),
    ],
  },
];

function Lightbox({ grid, idx, onMove, onClose }) {
  const boxRef = useRef(null);
  const tile = grid.tiles[idx];

  // Body scroll lock while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Focus trap + keyboard nav.
  useEffect(() => {
    const box = boxRef.current;
    box?.querySelector('button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onMove(1);
      else if (e.key === 'ArrowLeft') onMove(-1);
      else if (e.key === 'Tab') {
        const focusables = box.querySelectorAll('button');
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onMove]);

  return (
    <div className="ac-lightbox" role="dialog" aria-modal="true" aria-label={`${grid.kicker} — image ${idx + 1} of ${grid.tiles.length}`}
      ref={boxRef} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <button className="ac-lightbox-close" onClick={onClose} aria-label="Close">×</button>
      <button className="ac-lightbox-nav prev" onClick={() => onMove(-1)} aria-label="Previous image">←</button>
      <figure key={tile.src}>
        <img src={tile.src} alt={tile.alt} />
        <figcaption>
          <span>{tile.alt}</span>
          <span className="ac-lightbox-count">{String(idx + 1).padStart(2, '0')} / {grid.tiles.length}</span>
        </figcaption>
      </figure>
      <button className="ac-lightbox-nav next" onClick={() => onMove(1)} aria-label="Next image">→</button>
    </div>
  );
}

export default function StudioIndex() {
  const [open, setOpen] = useState(null); // { grid: index, idx: index }
  const lastTrigger = useRef(null);

  const close = useCallback(() => {
    setOpen(null);
    lastTrigger.current?.focus();
  }, []);
  const move = useCallback((dir) => {
    setOpen((o) => o && { ...o, idx: (o.idx + dir + GRIDS[o.grid].tiles.length) % GRIDS[o.grid].tiles.length });
  }, []);

  return (
    <section className="ac-section" id="studio-index" style={{ paddingTop: 0 }}>
      <div className="ac-wrap">
        <SectionHeader kicker="Studio index" title="The wider shelf." />
        <p className="ac-reveal" style={{ maxWidth: '58ch', marginTop: -28, marginBottom: 12 }}>
          Beyond the case studies — a running index of the studio&rsquo;s output
          across its three disciplines. Click anything to look closer.
        </p>
        {GRIDS.map((grid, gi) => (
          <div className="ac-mosaic-block" id={grid.id} key={grid.id}>
            <div className="ac-mosaic-head ac-reveal">
              <span className="ac-kicker">{grid.kicker}</span>
              <h3>{grid.title}</h3>
              <p>{grid.line}</p>
            </div>
            <div className="ac-mosaic">
              {grid.tiles.map((t, i) => (
                <button
                  key={t.src}
                  className="ac-mosaic-tile ac-reveal-scale"
                  style={{ '--d': `${(i % 4) * 0.06 + Math.floor(i / 4) * 0.03}s` }}
                  onClick={(e) => { lastTrigger.current = e.currentTarget; setOpen({ grid: gi, idx: i }); }}
                  aria-label={`View: ${t.alt}`}
                >
                  <img src={t.src} alt={t.alt} loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {open && <Lightbox grid={GRIDS[open.grid]} idx={open.idx} onMove={move} onClose={close} />}
    </section>
  );
}
